import type {
  WidgetMetrics,
  WidgetData,
  WidgetConfig,
  IdentifierType,
  WidgetTheme,
} from '../types';
import PlumXLogo from './PlumXLogo';
import PlumPrint from './PlumPrint';
import { buildDetailsLink } from '@plumx/utils';
import { groupByLabel, toDetailsLinkOptions } from '../utils/stats';
import { isAlternateTheme } from '../utils/theme';
import { CATEGORIES, visibleCategories } from '../utils/categories';
import { useTranslation } from '../i18n/i18n';
import styles from '../styles/widget.module.scss';

interface SummaryProps {
  metrics: WidgetMetrics;
  data: WidgetData;
  config: WidgetConfig;
  printSize?: 'xs' | 'sm' | 'md' | 'lg';
  identifier: string;
  identifierType: IdentifierType;
  theme?: WidgetTheme;
}

const Summary = ({
  metrics,
  data,
  config,
  printSize = 'lg',
  identifier,
  identifierType,
  theme,
}: SummaryProps) => {
  const { t } = useTranslation();
  const isVertical = config.summaryOrientation !== 'horizontal';
  const detailsLink = buildDetailsLink(identifier, identifierType, toDetailsLinkOptions(config));
  const hideHeader = isAlternateTheme(theme);

  return (
    <div className={styles.summaryCard} data-orientation={isVertical ? 'vertical' : 'horizontal'}>
      {!hideHeader && (
        <div className={styles.summaryHeader}>
          <PlumXLogo variant="white" size="md" />
        </div>
      )}
      {!config.hidePrint && (
        <div className={styles.summaryPrintArea}>
          <PlumPrint metrics={metrics} size={printSize} />
        </div>
      )}
      {visibleCategories(CATEGORIES, metrics, config).map(
        ({ label, metricsKey, statsKey, aggregation }) => {
          return (
            <div key={metricsKey} className={styles.summarySection} data-metric={metricsKey}>
              <div className={styles.summarySectionBar} />
              <div className={styles.summarySectionContent}>
                <div className={styles.summaryCategoryLabel}>{t(label)}</div>
                {groupByLabel(data.statistics[statsKey] ?? [], aggregation).map(
                  ({ label: statLabel, total }) => (
                    <div key={statLabel} className={styles.summaryStatRow}>
                      {t(statLabel)}: <strong>{total.toLocaleString()}</strong>
                    </div>
                  ),
                )}
              </div>
            </div>
          );
        },
      )}
      {!config.disableLinkToPlumx && (
        <div
          className={`${styles.summaryFooter}${hideHeader ? ` ${styles.summaryFooterWithLogo}` : ''}`}
        >
          {hideHeader && <PlumXLogo variant="color" size="sm" />}
          <a
            href={detailsLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.seeDetails}
          >
            {t('see details')}
            <svg
              className={styles.linkIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M18 109.855V18.14h36v10H28v71.715h72V74.14h9.996v35.715zM69.98 28.14v-10h40v40h-10V35.21l-35.8 35.82-7.08-7.08 35.81-35.81z"
                clipRule="evenodd"
              />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
};

export default Summary;
